import * as React from 'react';
import classnames from 'classnames';
import { ICoreContext } from '@msdyn365-commerce/core';
import { SimpleProduct, UnitOfMeasureConversion } from '@msdyn365-commerce/retail-proxy';
import { humanizeUnitOfMeasure } from '../unit-of-measure';
import { IAppSettingsInContext } from '../../settings/app.settings.autogenerated';
import { getTotalConversionText, shouldShowBreakoff } from '../../shared-utils/unit-conversion-utils';
import { ProductSearchResultExt, SimpleProductExt } from '../../shared-utils/types';
import { safeRemainder } from '../../shared-utils/math';

export interface IQuantityInputProps {
    mode?: 'edit' | 'view';
    allowBreakoffToggleInReadOnly?: boolean;
    context: ICoreContext<IAppSettingsInContext>;
    disable?: boolean;
    product: SimpleProduct | undefined;
    quantity: number;
    breakoff: boolean;
    nonBreakoffUOM?: string;
    onQuantityChange: (quantity: number, applyImmediately: boolean) => void;
    onBreakoffChange: (breakoff: boolean) => void;
    allowZero?: boolean;
    extProduct?: ProductSearchResultExt | SimpleProductExt | undefined;
    unitConversions?: UnitOfMeasureConversion[];
}

const onlyDigitsAndDecimalRegex = /[0-9.,]/;
const BeforeInputRules = [
    // Only allow digits and decimal point
    (str: string) => onlyDigitsAndDecimalRegex.test(str),
    // Only allow one leading zero before decimal point (or if no decimal point)
    (str: string) => str.startsWith('00') === false,
    // Only allow one decimal character
    (str: string) => [...str].filter(c => c === '.' || c === ',').length <= 1,
    // Only allow up to two decimal places
    (str: string) => str.indexOf('.') === -1 || str.split('.')[1]?.length <= 2
];

function isDecimalAllowed(product: SimpleProduct | undefined, breakoff: boolean): boolean {
    // Only relevant for kg products
    if (!product || product.DefaultUnitOfMeasure !== 'kg') {
        return false;
    }

    const multipleOfQuantity = product.Behavior?.MultipleOfQuantity;

    // If the product has breakoff available only allow if breakoff is enabled
    if (multipleOfQuantity && multipleOfQuantity > 1) {
        return breakoff;
    }

    // Otherwise always allow decimal
    return true;
}

const BreakoffToggle = ({
    context,
    baseClassName,
    mode,
    allowBreakoffToggleInReadOnly,
    disable,
    product,
    quantity,
    breakoff,
    unitConversions,
    onQuantityChange,
    onBreakoffChange
}: {
    context: ICoreContext;
    baseClassName: string;
    mode?: 'edit' | 'view';
    allowBreakoffToggleInReadOnly?: boolean;
    disable?: boolean;
    product: SimpleProduct | undefined;
    quantity: number;
    breakoff: boolean;
    unitConversions?: UnitOfMeasureConversion[];
    onQuantityChange: (quantity: number) => void;
    onBreakoffChange: (breakoff: boolean) => void;
}) => {
    // If user has manually changed breakoff option, we don't want to auto update it anymore
    const [forceBreakoff, setForceBreakoff] = React.useState<boolean>(false);
    const canBreakoff = React.useMemo(() => product?.Behavior?.MultipleOfQuantity && product.Behavior.MultipleOfQuantity > 1, [product]);

    React.useEffect(() => {
        if (!canBreakoff || forceBreakoff) {
            return;
        }

        const multiplier = product?.Behavior?.MultipleOfQuantity || 1;
        const newBreakoff = safeRemainder(quantity, multiplier) !== 0;

        if (newBreakoff !== breakoff) {
            onBreakoffChange?.(newBreakoff);
        }
    }, [quantity]);

    const onQuantityOptionChange = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, newBreakoff: boolean) => {
        setForceBreakoff(newBreakoff);

        if (newBreakoff === breakoff) {
            return;
        }

        // Redo variable here using new option
        const multiplier = product?.Behavior?.MultipleOfQuantity || 1;
        let newQuantity = quantity;

        if (!newBreakoff) {
            newQuantity = Math.trunc(quantity) * multiplier;
        } else {
            newQuantity = Math.ceil(quantity / multiplier);
        }

        onQuantityChange(newQuantity);
        onBreakoffChange?.(newBreakoff);

        // Look for sibling quantity input and focus it
        const quantityInput = (e.target as HTMLElement).closest(`.${baseClassName}`)?.parentElement?.querySelector(`.${baseClassName}__quantity-controls__input`);

        if (quantityInput) {
            (quantityInput as HTMLInputElement).select();
        }
    };

    if (!canBreakoff || !shouldShowBreakoff(context, product, unitConversions)) {
        return null;
    }

    return (
        <button
            className={classnames(`${baseClassName}__quantity-controls__breakoff`, { ['read-only']: (!allowBreakoffToggleInReadOnly && mode) === 'view' })}
            aria-pressed={breakoff}
            disabled={disable || !product}
            onClick={(e) => (allowBreakoffToggleInReadOnly || mode !== 'view') ? onQuantityOptionChange(e, !breakoff) : undefined}
        >
            {context.request.locale === 'en-US' ? 'BREAKOFF' : 'ANBREKK'}
        </button>
    );
};

const QuantityInputSkeleton = ({ baseClassName }: { baseClassName: string }) => {
    // Matching the structure of the actual QuantityInput but with shimmer placeholders
    const randomConversionWidth = React.useMemo(() => Math.floor(Math.random() * (85 - 27 + 1)) + 27, []); // Random width between 27 and 85

    return (
        <div className={baseClassName}>
            <div className={`${baseClassName}__quantity-controls`}>
                <div className={classnames(`${baseClassName}__quantity-controls__breakoff`, 'skeleton')}></div>
                <div className={classnames(`${baseClassName}__quantity-controls__input-container`, 'skeleton')}></div>
            </div>
            <div className={classnames(`${baseClassName}__conversion`, 'skeleton')} style={{ width: `${randomConversionWidth}px` }}></div>
        </div>
    );
};

const QuantityInput = (props: IQuantityInputProps) => {
    const {
        mode = 'edit',
        allowBreakoffToggleInReadOnly,
        context,
        disable,
        product,
        quantity,
        breakoff,
        nonBreakoffUOM,
        onQuantityChange: onQuantityChangeExternal,
        allowZero
    } = props;

    const inputRef = React.useRef<HTMLInputElement>(null);

    const allowDecimal = isDecimalAllowed(product, breakoff);
    const allowBelowOne = allowDecimal &&
        product?.DefaultUnitOfMeasure === 'kg' &&
        context.app.config?.allowFractionalQuantityForKgProductsInOUNs
            ?.split(',')
            .map(uom => uom.trim().toLowerCase())
            .includes(context.request.apiSettings.oun.toLowerCase());

    const onQuantityChange = (newQuantity: number, applyImmediately = false) => {
        if (quantity !== newQuantity || applyImmediately) {
            onQuantityChangeExternal(newQuantity, applyImmediately);
        }

        // Update the input value directly to avoid lag
        if (inputRef.current) {
            const multiplier = breakoff ? 1 : product?.Behavior?.MultipleOfQuantity || 1;
            const displayQuantity = parseFloat((newQuantity / multiplier).toFixed(2));

            // Preserve user entered decimals by only changing the value if it's different number wise
            if (parseFloat(inputRef.current.value) === displayQuantity) {
                return;
            }

            inputRef.current.value = displayQuantity.toString();
        }
    };

    React.useEffect(() => {
        if (!product || allowZero || quantity > 0) {
            return;
        }
        // Initial setup for quantity
        const multiplier = breakoff ? 1 : product.Behavior?.MultipleOfQuantity || 1;
        onQuantityChange(validateQuantity(1 * multiplier));
    }, [product]);

    React.useEffect(() => {
        if (!product) {
            return;
        }
        // Quantity was changed externally, update the input value
        const multiplier = breakoff ? 1 : product.Behavior?.MultipleOfQuantity || 1;
        const minimumQuantity = allowZero
            ? 0
            : (allowBelowOne && quantity > 0 ? 0.01 : 1) * multiplier;
        onQuantityChange(Math.max(minimumQuantity, quantity), false);
    }, [quantity]);

    const baseClassName = 'quantity-input-v2';
    // Check if there is a friendly UOM defined on the SimpleProduct
    const friendlyUOM = product?.ExtensionProperties?.find((x) => x.Key === 'FRIENDLYUOM')?.Value?.StringValue;
    // @ts-expect-error
    const breakoffUOM = humanizeUnitOfMeasure(context, friendlyUOM || product?.DefaultUnitOfMeasure, product);
    const uom = breakoff || !nonBreakoffUOM
        ? humanizeUnitOfMeasure(context, friendlyUOM || product?.DefaultUnitOfMeasure, product)
        : humanizeUnitOfMeasure(context, nonBreakoffUOM);

    // const selectedUOM = breakoff ? uom : 'KASSE(R)';
    const multiplier = breakoff ? 1 : product?.Behavior?.MultipleOfQuantity || 1;

    // const minimumQuantity = +!allowZero * multiplier;
    const minimumQuantity = allowZero
        ? 0
        : (allowBelowOne ? 0.01 : 1) * multiplier;

    const validateQuantity = (quantity: number): number => {
        const newQuantity = Math.max(minimumQuantity, quantity);
        return Math.round(newQuantity * 100) / 100; // Round to two decimal places
    };

    const onIncrement = () => {
        const newQuantity = validateQuantity(quantity + multiplier);
        onQuantityChange(newQuantity);
    };

    const onDecrement = () => {
        const newQuantity = validateQuantity(Math.max(0, quantity - multiplier));
        onQuantityChange(newQuantity);
    };

    const changeInputValue = (value: string, offsetStart: number = 0) => {
        if (inputRef.current && inputRef.current.value !== value) {
            const selectionStart = inputRef.current.selectionStart || 0;
            const selectionEnd = inputRef.current.selectionEnd || 0;

            // Set the value
            inputRef.current.value = value;

            // Restore the cursor position
            inputRef.current.setSelectionRange(selectionStart + offsetStart, selectionEnd + offsetStart);
        }
    };

    const onInputChange = (e: React.ChangeEvent<HTMLInputElement>, validate: boolean = false, applyImmediately = false) => {
        let rawValue = e.target.value;
        let offsetStart = 0;

        if (rawValue.includes(',')) {
            // Replace comma with dot and update input value
            rawValue = rawValue.replace(',', '.');
        }

        if (rawValue.startsWith('.')) {
            rawValue = `0${rawValue}`;
            offsetStart = 1;
        }

        // Normalize the input value
        changeInputValue(rawValue, offsetStart);

        const parsedValue = Math.min(9999, parseFloat(rawValue) || 0);

        // Do nothing if it looks like it's still being typed
        if (!validate && (rawValue === '' || parsedValue === 0 || rawValue.endsWith('.') || rawValue.startsWith('.'))) {
            return;
        }

        // Remove trailing dot
        if (rawValue.endsWith('.')) {
            changeInputValue(rawValue.slice(0, -1));
        }

        // We're essentially changing the displayQuantity instead of the actual quantity
        const newQuantity = breakoff
            ? validateQuantity(parsedValue)
            : validateQuantity(parsedValue * multiplier);

        onQuantityChange(newQuantity, applyImmediately);
    };

    const onBeforeInput = (e: React.FormEvent<HTMLInputElement>) => {
        e.persist();

        if (!inputRef.current) {
            return;
        }

        const data = (e.nativeEvent as InputEvent).data;
        const { selectionStart, selectionEnd, value } = inputRef.current;
        const proposedValue = value.substring(0, selectionStart || 0) +
            (data || '') +
            value.substring(selectionEnd || 0);

        if (proposedValue && !BeforeInputRules.every(rule => rule(proposedValue))) {
            e.preventDefault();
            return;
        }
    };

    const onKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        e.persist();

        if (!inputRef.current) {
            return;
        }

        // Only allow numbers and decimal point (if allowed)
        if ((!allowDecimal && (e.key === '.' || e.key === ',')) || !onlyDigitsAndDecimalRegex.test(e.key)) {
            e.preventDefault();
            return;
        }
    };

    const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        // If the input is empty, set it to 0
        if (e.target.value === '') {
            onInputChange({ target: { value: '0' } } as React.ChangeEvent<HTMLInputElement>, true, true);
        } else {
            onInputChange(e, true, true);
        }
    };

    const initialQuantity = product && parseFloat((quantity / multiplier).toFixed(2));
    const isM1ProductAndHasBreakoff = product?.DefaultUnitOfMeasure?.endsWith('-M1') && product.Behavior?.MultipleOfQuantity && product.Behavior.MultipleOfQuantity > 1;
    const conversionText = getTotalConversionText(context, product, props.unitConversions, quantity, breakoff, props.extProduct);

    if (!product) {
        // Shimmer placeholder
        return (
            <QuantityInputSkeleton baseClassName={baseClassName} />
        );
    }

    return (
        <div className={baseClassName}>
            <div className={`${baseClassName}__quantity-controls`}>
                <BreakoffToggle
                    context={context}
                    baseClassName={baseClassName}
                    mode={mode}
                    allowBreakoffToggleInReadOnly={allowBreakoffToggleInReadOnly}
                    disable={disable}
                    product={product}
                    quantity={quantity}
                    breakoff={breakoff}
                    unitConversions={props.unitConversions}
                    onQuantityChange={onQuantityChange}
                    onBreakoffChange={props.onBreakoffChange}
                />
                <div className={classnames(`${baseClassName}__quantity-controls__input-container`, { disabled: disable || !product, ['read-only']: mode === 'view' })}>
                    <button
                        className={classnames(`${baseClassName}__quantity-controls__minus`, { ['read-only']: mode === 'view' })}
                        disabled={disable || !product || quantity <= minimumQuantity}
                        onClick={onDecrement}
                    >
                        <svg width='15' height='15' viewBox='0 0 15 15' shapeRendering='geometricPrecision'>
                            <line x1='2.5' y1='7.5' x2='12.5' y2='7.5'></line>
                        </svg>
                    </button>
                    <div className={classnames(`${baseClassName}__quantity-controls__input-wrapper`, { disabled: disable || !product, ['read-only']: mode === 'view' })}>
                        <input
                            ref={inputRef}
                            className={`${baseClassName}__quantity-controls__input`}
                            readOnly={mode !== 'edit'}
                            disabled={disable || !product}
                            defaultValue={initialQuantity?.toString()}
                            min={minimumQuantity}
                            onChange={onInputChange}
                            onBeforeInput={onBeforeInput}
                            onKeyPress={onKeyPress}
                            onBlur={onBlur}
                            type='number'
                            inputMode='numeric'
                            aria-label='Antall'
                            data-nomodalcss
                        />
                        <span className={`${baseClassName}__quantity-controls__uom`} onClick={(e) => {
                            // Set focus to input above
                            const input = (e.target as HTMLElement).previousElementSibling as HTMLInputElement;
                            if (input) {
                                input.focus();
                            }
                        }}>
                            {!!isM1ProductAndHasBreakoff && `x ${product.Behavior!.MultipleOfQuantity} `}
                            {uom}
                        </span>
                    </div>
                    <button
                        className={classnames(`${baseClassName}__quantity-controls__plus`, { ['read-only']: mode === 'view' })}
                        disabled={disable || !product}
                        onClick={onIncrement}
                    >
                        <svg width='15' height='15' viewBox='0 0 15 15' shapeRendering='geometricPrecision'>
                            <line x1='2.5' y1='7.5' x2='12.5' y2='7.5'></line>
                            <line x1='7.5' y1='2.5' x2='7.5' y2='12.5'></line>
                        </svg>
                    </button>
                </div>
            </div>
            <div className={`${baseClassName}__conversion`}>
                {conversionText?.split('\n').map((line, index) => (
                    <>
                        {index > 0 && <span className={`${baseClassName}__conversion-separator`}>·</span>}
                        <span key={index}>{line}</span>
                    </>
                ))}
            </div>
        </div>
    );

    // @ts-expect-error
    return (
        <div className={baseClassName}>
            <div className={`${baseClassName}__quantity-container`}>
                <div className={`${baseClassName}__quantity-controls`}>
                    {mode === 'edit' && (
                        <button
                            className={`${baseClassName}__quantity-controls__minus`}
                            disabled={disable || !product || quantity === minimumQuantity}
                            onClick={onDecrement}
                        >
                            -
                        </button>
                    )}
                    <input
                        ref={inputRef}
                        className={classnames(`${baseClassName}__quantity-controls__input`, mode)}
                        readOnly={mode !== 'edit'}
                        disabled={disable || !product}
                        defaultValue={initialQuantity?.toString()}
                        min={minimumQuantity}
                        onBeforeInput={onBeforeInput}
                        onKeyPress={onKeyPress}
                        onChange={onInputChange}
                        onBlur={onBlur}
                        data-nomodalcss />
                    <span className={classnames(`${baseClassName}__quantity-controls__uom`, { disabled: disable })} onClick={(e) => {
                        // Set focus to input above
                        const input = (e.target as HTMLElement).previousElementSibling as HTMLInputElement;
                        if (input) {
                            input.focus();
                        }
                    }}>
                        {!!isM1ProductAndHasBreakoff && `x ${product!.Behavior!.MultipleOfQuantity} `}
                        {uom}
                    </span>
                    {mode === 'edit' && (
                        <button
                            className={`${baseClassName}__quantity-controls__plus`}
                            disabled={disable || !product}
                            onClick={onIncrement}
                        >
                            +
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default QuantityInput;
